import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { confirm, haptic } from '../lib/telegram';
import AmountInput from '../components/AmountInput';
import { formatAmount, parseAmountInput } from '../lib/format';
import type { Goal, Currency } from '../lib/types';

interface Props { onBack: () => void; }

export default function Goals({ onBack }: Props) {
  const [goals, setGoals] = useState<Goal[]>([]);
  const [name, setName] = useState('');
  const [currency, setCurrency] = useState<Currency>('SYP');
  const [target, setTarget] = useState('');
  const [contrib, setContrib] = useState<Record<number, string>>({});
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  async function load() {
    setErr(null);
    try {
      const r = await api.listGoals();
      setGoals(r.goals);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'failed to load');
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  async function add(e: React.FormEvent) {
    e.preventDefault();
    const t = parseAmountInput(target);
    if (!name.trim()) { setErr('Enter a goal name'); return; }
    if (t === null) { setErr('Target must be positive'); return; }
    setErr(null); setBusy(true);
    try {
      await api.addGoal({ name: name.trim(), currency, target_amount: t });
      setName(''); setTarget('');
      haptic('success');
      await load();
    } catch (e) {
      haptic('error');
      setErr(e instanceof Error ? e.message : 'failed');
    } finally { setBusy(false); }
  }

  async function contribute(g: Goal) {
    const amt = parseAmountInput(contrib[g.id] || '');
    if (amt === null) { setErr('Enter a contribution amount'); return; }
    setErr(null);
    try {
      await api.contributeGoal(g.id, amt);
      setContrib((c) => ({ ...c, [g.id]: '' }));
      haptic('success');
      await load();
    } catch (e) {
      haptic('error');
      setErr(e instanceof Error ? e.message : 'failed');
    }
  }

  async function remove(g: Goal) {
    if (!(await confirm(`Delete the goal "${g.name}"?`))) return;
    try { await api.removeGoal(g.id); haptic('success'); await load(); }
    catch (e) { setErr(e instanceof Error ? e.message : 'failed'); }
  }

  return (
    <>
      <div className="header">
        <button className="back" onClick={onBack}>← Back</button>
        <h1>Savings goals</h1>
      </div>
      {err && <div className="error">{err}</div>}

      <form className="card" onSubmit={add}>
        <div className="section-title">New goal</div>
        <label className="label" style={{ marginTop: 0 }}>Name</label>
        <input className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. New phone" maxLength={50} />
        <label className="label">Currency</label>
        <div className="segmented">
          <button type="button" className={currency === 'SYP' ? 'active' : ''} onClick={() => setCurrency('SYP')}>SYP</button>
          <button type="button" className={currency === 'USD' ? 'active' : ''} onClick={() => setCurrency('USD')}>USD</button>
        </div>
        <label className="label">Target amount</label>
        <AmountInput value={target} onChange={setTarget} currency={currency} />
        <button type="submit" className="btn" style={{ marginTop: 12 }} disabled={busy}>
          {busy ? 'Saving…' : 'Add goal'}
        </button>
      </form>

      {loading ? (
        <div className="card"><div className="muted">Loading…</div></div>
      ) : goals.length === 0 ? (
        <div className="card"><div className="empty">No goals yet — add one above.</div></div>
      ) : goals.map((g) => {
        const saved = Number(g.saved_amount);
        const tgt = Number(g.target_amount);
        const pct = tgt > 0 ? Math.min((saved / tgt) * 100, 100) : 0;
        const done = !!g.achieved_at;
        return (
          <div className="card" key={g.id}>
            <div className="cat-head">
              <span className="cat-name">{done ? '🎉 ' : ''}{g.name}</span>
              <button onClick={() => remove(g)} style={{ background: 'transparent', border: 'none', color: 'var(--destructive)', cursor: 'pointer' }}>✕</button>
            </div>
            <div className="muted small" style={{ marginBottom: 6 }}>
              {formatAmount(saved, g.currency)} of {formatAmount(tgt, g.currency)} · {pct.toFixed(0)}%
            </div>
            <div className="cat-bar">
              <div className="cat-bar-fill" style={{ width: `${pct}%`, background: done ? '#2e8b57' : 'var(--button-bg)' }} />
            </div>
            {!done && (
              <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                <input
                  className="input"
                  inputMode="decimal"
                  placeholder="Add amount"
                  value={contrib[g.id] || ''}
                  onChange={(e) => setContrib((c) => ({ ...c, [g.id]: e.target.value }))}
                />
                <button
                  className="btn"
                  style={{ width: 'auto', padding: '12px 16px' }}
                  onClick={() => contribute(g)}
                >＋ Add</button>
              </div>
            )}
          </div>
        );
      })}

      <p className="muted small" style={{ textAlign: 'center' }}>
        Goals track progress you log manually — they don't move your cash balance.
      </p>
    </>
  );
}
